import {
  CircularProgress,
  Grid,
  IconButton,
  InputAdornment,
  ListItemIcon,
  ListItemText,
  MenuItem,
  MenuList,
  Modal,
  Popover,
  Radio,
  RadioGroup,
  Tooltip,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import * as S from "./styles";
import { useEffect, useMemo, useRef, useState } from "react";
import {
  chooseInstallments,
  creditCardBrands,
  mobileNav,
  navList,
} from "./data";
import { Link, Outlet, useNavigate } from "react-router-dom";
import { useColorContext } from "@/styles/themes/default";
import logo from "@/assets/svgs/logo.svg";
import { AiOutlineDown, AiOutlineMenu, AiOutlineUser } from "react-icons/ai";
import { BiLogIn, BiLogOut } from "react-icons/bi";
import { useAuth } from "@/context/AuthContext";
import { api } from "@/services/api";
import { toast } from "react-toastify";
import { UseUser } from "@/context/UserContext";
import { BsArrowLeft, BsFillCreditCard2BackFill } from "react-icons/bs";
import { FutButton } from "@/components/FutButton";
import PlansChooser from "@/components/PlansChooser";
import { PlansModal } from "@/components/PlansModal";
import { Controller, SubmitHandler, useForm } from "react-hook-form";
import {
  plans,
  plansMonths,
  plansName,
  plansPrices,
  rawPlanPrices,
} from "@/constants/plans";
import { FaCcVisa, FaPix } from "react-icons/fa6";

import { ReactComponent as SuccessSvg } from "@/assets/svgs/success-circle.svg";
import { catchError } from "@/utils/catchError";
import { setCookie } from "@/utils/cookies";
import PatternInput from "@/components/PatternInput";

import creditCardType, {
  getTypeInfo,
  types as CardType,
} from "credit-card-type";
import RoundInput from "@/components/RoundInput";
import { IoMdArrowRoundBack, IoMdHelpCircleOutline } from "react-icons/io";

import * as yup from "yup";
import { yupResolver } from "@hookform/resolvers/yup";
import { REQUIRED_FIELD, VALID_CPF } from "@/constants/yupErrors";
import { cpfIsValid } from "@/utils/cpfValidator";
import { AxiosError } from "axios";
import FutSelect from "@/components/FutSelect";
import { IoArrowBackOutline, IoClose } from "react-icons/io5";
import LanguageSelector from "@/components/LanguageSelector";
import { useTranslation } from "react-i18next";

declare global {
  interface Window {
    fbq: any;
  }
}

const creditCardSchema = yup.object({
  creditCard: yup.object({
    holderName: yup.string().required(REQUIRED_FIELD),
    number: yup.string().required(REQUIRED_FIELD),
    expiryDate: yup.string().required(REQUIRED_FIELD),
    ccv: yup.string().required(REQUIRED_FIELD),
  }),
  creditCardHolderInfo: yup.object({
    name: yup.string(),
    email: yup.string(),
    cpfCnpj: yup
      .string()
      .required(REQUIRED_FIELD)
      .test(
        "is-cpf-valid",
        () => VALID_CPF,
        (value) => cpfIsValid(value)
      ),
    postalCode: yup
      .string()
      .required(REQUIRED_FIELD)
      .test("is-cep-valid", "Cep Invalido", (val) => {
        if (val.replace(/\D/g, "").length < 8) {
          return false;
        }
        return true;
      }),
    addressNumber: yup.string().required(REQUIRED_FIELD),
    phone: yup.string(),
  }),
});

type creditCardType = yup.InferType<typeof creditCardSchema>;
export function Dashboard() {
  const { t } = useTranslation();
  const theme = useTheme();
  const isMedia1200 = !useMediaQuery(theme.breakpoints.up("lg"));
  const [anchorEl, setAnchorEl] = useState<HTMLDivElement | null>(null);
  const [step, setStep] = useState(0);
  const [planType, setPlanType] = useState<keyof typeof plans>("monthly");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const { isAuthenticated, signOut, decodedToken, changeToken } = useAuth()
  const [svgCardType, setSvgCardType] = useState<
    | React.FunctionComponentElement<
      React.SVGProps<SVGSVGElement> & {
        title?: string | undefined;
      }
    >
    | ""
  >("");
  const containerRef = useRef<HTMLDivElement>(null);

  const { balance, paymentModal, setPaymentModal } = UseUser();
  const isOpen = Boolean(anchorEl);
  const [isMobileOpen, setIsMobileOpen] = useState(false);
  
  // Fechar mobile menu quando modal de pagamento abrir
  useEffect(() => {
    if (paymentModal && isMobileOpen) {
      setIsMobileOpen(false);
    }
  }, [paymentModal, isMobileOpen]);
  const [fbqTrackers, setFbqTrackers] = useState({
    initCheckout: false,
    paymentInfo: false,
  });

  const fbqExists = useMemo(() => {
    if (typeof window.fbq !== "undefined") {
      return true;
    }
    return false;
  }, [typeof window.fbq]);

  const paymentForm = useForm({
    defaultValues: {
      payment: "CREDIT_CARD",
    },
  });

  const creditCardForm = useForm({
    defaultValues: {
      creditCardHolderInfo: {
        name: "",
        email: "",
        cpfCnpj: "",
        postalCode: "",
        addressNumber: "",
      },
      creditCard: {
        holderName: "",
        number: "",
        expiryDate: "",
        ccv: "",
      },
      installment: 1,
    },
    resolver: yupResolver(creditCardSchema),
  });

  const watchCredNumber = creditCardForm.watch("creditCard.number");

  useEffect(() => {
    if (watchCredNumber.replace(/\D/g, "").length >= 4) {
      const type = creditCardType(watchCredNumber.replace(/\D/g, ""))[0]
        .type as keyof typeof creditCardBrands;
      setSvgCardType(creditCardBrands[type]);

      if (!fbqTrackers.paymentInfo && fbqExists) {
        window.fbq("track", "AddPaymentInfo", { plan: plansName[planType] });
        setFbqTrackers((prev) => ({ ...prev, paymentInfo: true }));
      }
      return;
    }
    setSvgCardType("");
  }, [watchCredNumber]);

  // Debug log to check menu state
  useEffect(() => {
    console.log('Menu state changed:', isMobileOpen);
  }, [isMobileOpen]);

  // Ensure menu starts closed
  useEffect(() => {
    setIsMobileOpen(false);
  }, []);

  // Clean up body styles on unmount or when screen size changes
  useEffect(() => {
    // Close mobile menu and restore scroll when screen gets larger
    if (!isMedia1200 && isMobileOpen) {
      setIsMobileOpen(false);
      document.body.style.overflow = '';
      document.body.style.position = '';
      document.body.style.width = '';
    }
  }, [isMedia1200, isMobileOpen]);

  // Clean up on unmount
  useEffect(() => {
    return () => {
      // Restore body styles when component unmounts
      document.body.style.overflow = '';
      document.body.style.position = '';
      document.body.style.width = '';
    };
  }, []);

  const creditCardErrors = creditCardForm.formState.errors;

  const navigate = useNavigate();

  const handleMobile = () => {
    setIsMobileOpen((prev) => {
      const newState = !prev;
      
      // Prevent body scroll when menu is open
      if (newState) {
        document.body.style.overflow = 'hidden';
        document.body.style.position = 'fixed';
        document.body.style.width = '100%';
      } else {
        document.body.style.overflow = '';
        document.body.style.position = '';
        document.body.style.width = '';
      }
      
      return newState;
    });
  };

  const planHandler = async (plan: keyof typeof plans) => {
    setIsSubmitting(true);
    setPlanType(plan);
    if (!fbqTrackers.initCheckout && fbqExists) {
      window.fbq("track", "InitiateCheckout", { plan: plansName[plan] });
      setFbqTrackers((prev) => ({ ...prev, initCheckout: true }));
    }
    changeStep("next");
  };

  const changeStep = (toStep: "prev" | "next") => {
    if (containerRef.current) {
      containerRef.current.style.opacity = "0";

      setTimeout(() => {
        if (toStep === "next") {
          setStep((prev) => prev + 1);
        }

        if (toStep === "prev") {
          setStep((prev) => prev - 1);
        }

        containerRef.current!.style.opacity = "1";
        setIsSubmitting(false);
      }, 400);
    }
  };

  const submitPayment = async (data: { payment: string }) => {
    try {
      setIsSubmitting(true);
      if (data.payment === "PIX") {
        const response = await api.post("/payment/checkout", {
          subscriptionId: plans[planType as keyof typeof plans],
          userId: decodedToken.id,
          type_payment: data.payment,
        });

        setCookie(
          "payment_data",
          JSON.stringify({
            userId: decodedToken.id,
            planType: planType,
            pixKey: response.data,
          }),
          1
        );

        navigate("/telaPix");
      }

      if (data.payment === "CREDIT_CARD") {
        changeStep("next");
      }

      setIsSubmitting(false);
    } catch (e) {
      setStep(0);
      setPaymentModal(false);
      setIsSubmitting(false);
      const message = catchError(e);
      if (message) {
        toast.error(message);
        return;
      }
      toast.error("Ocorreu um erro inesperado ao enviar sua solicitação");
    }
  };

  const submitCreditCard: SubmitHandler<creditCardType> = async (data) => {
    setIsSubmitting(true);
    try {
      data.creditCard.ccv = data.creditCard.ccv.trim();
      data.creditCardHolderInfo.email = decodedToken.email;
      data.creditCardHolderInfo.name = data.creditCard.holderName;

      const ip = await api.get("user/ip");
      await api.post("/payment/checkout", {
        ...data,
        subscriptionId: plans[planType as keyof typeof plans],
        userId: decodedToken.id,
        type_payment: "CREDIT_CARD",
        remoteIp: ip.data.ip,
      });
      if (fbqExists) {
        window.fbq("track", "Purchase", {
          name: data.creditCardHolderInfo.name,
          email: data.creditCardHolderInfo.email,
          postalCode: data.creditCardHolderInfo.postalCode,
          plan: plansName[planType],
        });
      }
      try {
        const newToken = await api.get("/user/updateToken");
        changeToken(newToken.data.token);
        changeStep("next")
        setTimeout(() => {
          location.reload();
        }, 5000)
      } catch (e) {
        toast.error(
          "Ocorreu um erro, tente deslogar e logar novamente para consertar",
          { autoClose: 60000 }
        );
        setIsSubmitting(false);
        setPaymentModal(false);
      }
    } catch (error) {
      setIsSubmitting(false);
      const err = error as AxiosError<{
        error: { message: string; code: string };
      }>;
      const message = err.response?.data.error.message;
      if (message) {
        toast.error(message, { autoClose: 20000 });
        return;
      }
      toast.error("Ocorreu um erro ao tentar fazer a requisição");
    }
  };

  const modalSteps = [
    // {
    //   component: (
    //     <S.ValidSignature>
    //       <h2>Sua Assinatura Não é Valida</h2>

    //       <p>
    //         Parece que sua assinatura acabou ou você ainda não fez o pagamento,
    //         volte agora a ser um membro da equipe e tenha todos os benefícios
    //         que a Futscore te oferece.
    //       </p>

    //       <FutButton fullWidth onClick={() => changeStep("next")}>
    //         Assinar Agora
    //       </FutButton>
    //     </S.ValidSignature>
    //   ),
    // },
        {
          component: (
            <>
              <PlansChooser
                planHandler={(plan) => planHandler(plan)}
                disabled={isSubmitting}
                showModal={true}
              />
            </>
          ),
        },
    {
      component: (
        <S.PaymentContainer onSubmit={paymentForm.handleSubmit(submitPayment)}>
          <Controller
            name="payment"
            control={paymentForm.control}
            render={({ field }) => (
              <RadioGroup sx={{ width: "250px" }} {...field}>
                <S.RadioContainer>
                  <Radio value="CREDIT_CARD" />
                  <S.RadioNameContainer>
                    <BsFillCreditCard2BackFill />
                    {t('payment.creditCard')}
                  </S.RadioNameContainer>
                </S.RadioContainer>

                <S.RadioContainer>
                  <Radio value="PIX" />
                  <S.RadioNameContainer>
                    <FaPix />
                    {t('payment.pix')}
                  </S.RadioNameContainer>
                </S.RadioContainer>

                <FutButton
                  type="submit"
                  isLoading={isSubmitting}
                  fullWidth
                  sx={{ mt: 2 }}
                >
                  {t('payment.proceedPurchase')}
                </FutButton>
              </RadioGroup>
            )}
          />
        </S.PaymentContainer>
      ),
    },
    {
      component: (
        <S.CreditCardContainer>
          <Grid container spacing={0.5}>
            <Grid item xs={12}>
              <PatternInput
                inputName="creditCard.number"
                control={creditCardForm.control}
                fullWidth
                format="#### #### #### ####"
                placeholder="1111 1111 1111 1111"
                errorText={creditCardErrors.creditCard?.number}
                topLabel="Número do cartão de crédito"
                size="small"
                endAdornment={
                  <InputAdornment position="end">{svgCardType}</InputAdornment>
                }
              />
            </Grid>
            <Grid item md={6} xs={12}>
              <PatternInput
                inputName="creditCard.expiryDate"
                control={creditCardForm.control}
                fullWidth
                format="##/####"
                mask="_"
                placeholder="MM/AAAA"
                errorText={creditCardErrors.creditCard?.expiryDate}
                topLabel="Data de validade"
                size="small"
              />
            </Grid>
            <Grid item md={6} xs={12}>
              <PatternInput
                inputName="creditCard.ccv"
                control={creditCardForm.control}
                fullWidth
                format="####"
                placeholder="123"
                errorText={creditCardErrors.creditCard?.ccv}
                topLabel="CCV/CVC"
                sx={{ pr: 0 }}
                endAdornment={
                  <InputAdornment position="end">
                    <Tooltip
                      title="O CCV/CVC são os 3 ou 4 numeros atrás do seu cartão"
                      arrow
                      placement="top"
                    >
                      <IconButton>
                        <IoMdHelpCircleOutline />
                      </IconButton>
                    </Tooltip>
                  </InputAdornment>
                }
                size="small"
              />
            </Grid>
            <Grid item sm={12} xs={12}>
              <RoundInput
                inputName="creditCard.holderName"
                control={creditCardForm.control}
                fullWidth
                placeholder="Nome e Sobrenome"
                errorText={creditCardErrors.creditCard?.holderName}
                topLabel="Nome no Cartão"
                size="small"
                autoComplete="email"
              />
            </Grid>
            <Grid item sm={6} xs={12}>
              <PatternInput
                inputName="creditCardHolderInfo.postalCode"
                control={creditCardForm.control}
                fullWidth
                format="#####-###"
                placeholder="12345-678"
                errorText={creditCardErrors.creditCardHolderInfo?.postalCode}
                topLabel="CEP"
                mask="_"
                size="small"
              />
            </Grid>
            <Grid item sm={6} xs={12}>
              <RoundInput
                inputName="creditCardHolderInfo.addressNumber"
                control={creditCardForm.control}
                fullWidth
                errorText={creditCardErrors.creditCardHolderInfo?.addressNumber}
                topLabel="Número do endereço"
                size="small"
                autoComplete="email"
              />
            </Grid>
            <Grid item sm={12} xs={12}>
              <PatternInput
                inputName="creditCardHolderInfo.cpfCnpj"
                control={creditCardForm.control}
                fullWidth
                format="###.###.###-##"
                mask="_"
                placeholder="000.000.000-00"
                errorText={creditCardErrors.creditCardHolderInfo?.cpfCnpj}
                topLabel="CPF do dono cartão"
                size="small"
              />
            </Grid>
            <Grid item xs={12} my={1}>
              {planType === "monthly" ? (
                ""
              ) : (
                <FutSelect
                  control={creditCardForm.control}
                  inputName="installment"
                  data={
                    planType === "semiannual"
                      ? chooseInstallments(6, rawPlanPrices.semiannual)
                      : chooseInstallments(12, rawPlanPrices.annual)
                  }
                  onChange={(e) => {
                    creditCardForm.setValue(
                      "installment",
                      e.target.value as number
                    );
                  }}
                  sx={{ width: "100%" }}
                  defaultValue={1}
                />
              )}
            </Grid>
            <Grid item xs={12}>
              <S.ChangePlanContainer>
                <div>
                  <h2>{plansName[planType]}</h2>
                  <div>
                    <p>{plansPrices[planType]}</p>
                    <small>/{plansMonths[planType]}</small>
                  </div>
                </div>
                <div>
                  <FutButton
                    onClick={() => {
                      changeStep("prev");
                      changeStep("prev");
                    }}
                    variant="text"
                    fullWidth
                    sx={{ mt: 2 }}
                  >
                    Trocar Plano
                  </FutButton>
                </div>
              </S.ChangePlanContainer>
            </Grid>
            <Grid item xs={12}>
              <FutButton
                onClick={creditCardForm.handleSubmit(submitCreditCard)}
                isLoading={isSubmitting}
                fullWidth
                sx={{ mt: 2 }}
              >
                Finaliza Compra
              </FutButton>
            </Grid>
          </Grid>
        </S.CreditCardContainer>
      ),
    },
    {
      component: (
        <>
          {isSubmitting ? (
            <S.SuccessContainer style={{ padding: 30 }}>
              <CircularProgress />
            </S.SuccessContainer>
          ) : (
            <S.SuccessContainer>
              <SuccessSvg />
              <h1>Sucesso!</h1>
              <p>
                Espere alguns segundos enquanto estamos dando os retoques
                finais!
              </p>
            </S.SuccessContainer>
          )}
        </>
      ),
    },
  ];

  return (
    <>
      <S.Navigation mobileOpen={isMobileOpen}>
        {isAuthenticated ? (
          <>
            <S.LogoContainer isActive={decodedToken.isActive}>
              <Link to={"/dashboard"}>
                <img src={logo} alt="Precision" />
              </Link>

              <h2>{decodedToken.isActive ? "Premium" : "Free"}</h2>
            </S.LogoContainer>

            {isMedia1200 ? (
              <S.MobileContainer>
                <S.MobileHamburgerButton onClick={handleMobile}>
                  <AiOutlineMenu />
                </S.MobileHamburgerButton>
                <S.MobileMenu isOpen={isMobileOpen}>
                  <S.MobileMenuContent onClick={(e) => e.stopPropagation()}>
                    <S.CloseButton onClick={handleMobile} aria-label="Fechar menu">
                      <h3>X</h3>
                    </S.CloseButton>
                    <h2>{t('menu.title')}</h2>
                    <S.MobileCard>
                      {mobileNav.map((el) => (
                        <S.LinkCard
                          to={decodedToken.isActive ? el.link : "#"}
                          key={el.text}
                          onClick={() => {
                            if (!decodedToken.isActive) {
                              setPaymentModal(true);
                              return;
                            }
                            handleMobile();
                          }}
                        >
                          <li>
                            <p>
                              {el.icon} {t(`menu.${el.text.toLowerCase().replace(/\s+/g, '')}`)}
                            </p>
                          </li>
                        </S.LinkCard>
                      ))}
                      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', marginTop: '10px' }}>
                        <LanguageSelector />
                      </div>
                      <S.LinkCard to="#" onClick={signOut}>
                        <li>
                          <p style={{ color: "red" }}>
                            <BiLogOut /> {t('menu.logout')}
                          </p>
                        </li>
                      </S.LinkCard>
                    </S.MobileCard>
                    {/* removed bottom close button to avoid duplication */}
                  </S.MobileMenuContent>
                </S.MobileMenu>
              </S.MobileContainer>
            ) : (
              <S.NavItens>
                <S.ButtonGroup>
                  {navList.map((el) => (
                    <S.NavButton
                      to={!decodedToken.isActive ? "#" : el.link}
                      onClick={
                        !decodedToken.isActive
                          ? () => setPaymentModal(true)
                          : undefined
                      }
                      end
                      key={el.text}
                      defaultChecked={el.link === "/dashboard"}
                      removeActive={
                        el.link === "/dashboard" ? false : !decodedToken.isActive
                      }
                    >
                      {t(`menu.${el.text.toLowerCase().replace(/\s+/g, '')}`)}
                    </S.NavButton>
                  ))}
                </S.ButtonGroup>

                <LanguageSelector />
                <S.ProfileStats onClick={(e) => setAnchorEl(e.currentTarget)}>
                  <S.ProfileImg />
                  <S.WalletAmount>
                    <p>{t('menu.wallet')}</p>
                    <span>
                      {balance.balance === ""
                        ? t('common.loading')
                        : new Intl.NumberFormat("pt-BR", {
                          style: "currency",
                          currency: "BRL",
                        }).format(balance.balance)}
                    </span>
                  </S.WalletAmount>
                  <AiOutlineDown />
                </S.ProfileStats>
                <Popover
                  id={isOpen ? "open" : undefined}
                  open={isOpen}
                  anchorEl={anchorEl}
                  onClose={() => setAnchorEl(null)}
                  anchorOrigin={{
                    vertical: "bottom",
                    horizontal: "center",
                  }}
                  transformOrigin={{
                    vertical: "top",
                    horizontal: "center",
                  }}
                >
                  <MenuList style={{ maxWidth: "100%", width: 150 }}>
                    <MenuItem
                      onClick={() => {
                        if (!decodedToken.isActive) {
                          setPaymentModal(true);
                          return;
                        }
                        navigate("perfil");
                        setAnchorEl(null);
                      }}
                    >
                      <ListItemIcon>
                        <AiOutlineUser />
                      </ListItemIcon>
                      <ListItemText>{t('menu.profile')}</ListItemText>
                    </MenuItem>
                    <MenuItem
                      onClick={() => {
                        signOut();
                      }}
                      sx={{ color: "red" }}
                    >
                      <ListItemIcon sx={{ color: "red" }}>
                        <BiLogOut />
                      </ListItemIcon>
                      <ListItemText>{t('menu.logout')}</ListItemText>
                    </MenuItem>
                  </MenuList>
                </Popover>
              </S.NavItens>
            )}
          </>
        ) : (
          <>
            <S.LogoContainer isActive={false}>
              <Link to={"/"}>
                <img src={logo} alt="Precision" />
              </Link>
            </S.LogoContainer>
            <S.NavItens>
                <FutButton
                  onClick={() => navigate("/login")}
                  startIcon={<BiLogIn />}
                  >
                  {t('auth.login')}
                </FutButton>
            </S.NavItens>
          </>
        )}
      </S.Navigation>

      <Outlet />

      <svg
        style={{ visibility: "hidden", position: "absolute" }}
        width="0"
        height="0"
        xmlns="http://www.w3.org/2000/svg"
        version="1.1"
      >
        <defs>
          <filter id="roundHex">
            <feGaussianBlur in="SourceGraphic" stdDeviation="1" result="blur" />
            <feColorMatrix
              in="blur"
              mode="matrix"
              values="1 0 0 0 0  0 1 0 0 0  0 0 1 0 0  0 0 0 19 -9"
              result="roundHex"
            />
            <feComposite in="SourceGraphic" in2="roundHex" operator="atop" />
          </filter>
        </defs>
      </svg>

      <Modal 
        open={paymentModal} 
        onClose={() => setPaymentModal(false)}
        sx={{
          zIndex: 10001,
          '& .MuiBackdrop-root': {
            backgroundColor: 'rgba(0, 0, 0, 0.8)',
          }
        }}
      >
        <S.ModalWrapper>
          <S.ModalContainer ref={containerRef}>
            {isSubmitting ? null : (
              <S.ModalActions>
                {step > 0 && step < 2 ? (
                  <S.ModalButton onClick={() => changeStep("prev")}>
                    <IoArrowBackOutline />
                  </S.ModalButton>
                ) : null}

                <S.ModalButton onClick={() => setPaymentModal(false)} style={{right: "10px", left: "unset"}}>
                  <IoClose />
                </S.ModalButton>
              </S.ModalActions>
            )}
            {modalSteps[step].component}
          </S.ModalContainer>
        </S.ModalWrapper>
      </Modal>
    </>
  );
}
